import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Timer, TrendingDown, Zap, Sparkles, AlertCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { LoginToBuy } from './LoginToBuy';
import { getProductImageMedium } from '../../data/productImages';
import type { Product } from '../../types';

type Tag = 'OFFERTA' | 'NOVITA' | 'OUTLET';

interface Props {
  product: Product;
  tag?: Tag;
  /** ISO date — countdown end. Solo per OFFERTA. */
  endDate?: string;
  priceCurrent: string;
  priceOld?: string;
  stock: number;
}

function useCountdown(targetIso?: string) {
  const target = useMemo(() => (targetIso ? new Date(targetIso).getTime() : 0), [targetIso]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!targetIso) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  const diff = Math.max(0, target - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    expired: diff === 0,
  };
}

export const FeaturedProductCard: React.FC<Props> = ({ product, tag, endDate, priceCurrent, priceOld, stock }) => {
  const { addItem, updateQty, items } = useCart();
  const nonVendibile = product.webSellable === false;
  const { isAuthenticated } = useAuth();
  const cartItem = items.find(i => i.sku === product.sku);
  const cartQty = cartItem?.qty || 0;
  const inCart = cartQty > 0;
  const isOffer = tag === 'OFFERTA';
  const isOutlet = tag === 'OUTLET';

  const curNum = parseFloat(priceCurrent.replace(',', '.'));
  const oldNum = priceOld ? parseFloat(priceOld.replace(',', '.')) : 0;
  const sconto = oldNum > 0 ? Math.round(((oldNum - curNum) / oldNum) * 100) : 0;
  const risparmio = oldNum > 0 ? oldNum - curNum : 0;

  const stockPerc = Math.min(100, Math.max(stock > 0 ? 15 : 0, stock * 8));
  const tid = `featured-card-${product.sku}`;

  const { days, hours, minutes, seconds, expired } = useCountdown(isOffer ? endDate : undefined);

  // Safety net: se l'offerta è scaduta non renderizziamo nulla
  if (isOffer && expired) return null;

  const borderClass = isOffer
    ? 'border-amber-200'
    : isOutlet
      ? 'border-red-200'
      : tag === 'NOVITA'
        ? 'border-emerald-200'
        : 'border-border';

  return (
    <div
      data-testid={tid}
      className={`relative bg-white rounded-2xl border-2 ${borderClass} shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 overflow-hidden group flex flex-col`}
    >
      {/* Badge tag — solo se esplicitamente passato */}
      {tag && (
      <div className="absolute top-3 left-3 z-10">
        {isOffer ? (
          <div className="bg-gradient-to-br from-[#1E4D8C] to-[#2B5EA7] text-white text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-lg shadow-md flex items-center gap-1">
            <Zap className="w-2.5 h-2.5" fill="currentColor" />
            OFFERTA
          </div>
        ) : isOutlet ? (
          <div className="bg-gradient-to-br from-red-600 to-red-700 text-white text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-lg shadow-md flex items-center gap-1">
            <AlertCircle className="w-2.5 h-2.5" fill="currentColor" />
            OUTLET
          </div>
        ) : (
          <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-lg shadow-md flex items-center gap-1">
            <Sparkles className="w-2.5 h-2.5" fill="currentColor" />
            NOVITÀ
          </div>
        )}
      </div>
      )}

      {/* Badge % sconto — offerte e outlet con vecchio prezzo */}
      {(isOffer || isOutlet) && sconto > 0 && (
        <div className="absolute top-3 right-3 z-10">
          <div className="bg-red-500 text-white w-11 h-11 rounded-full flex items-center justify-center shadow-md font-bold leading-none text-xs">
            -{sconto}%
          </div>
        </div>
      )}

      {/* Immagine — stessa logica di dettaglio/vista (getProductImageMedium per category) */}
      <Link
        to={`/prodotti/${product.category}/${product.sku}`}
        className="block bg-gradient-to-br from-secondary/30 to-secondary/10 h-36 flex items-center justify-center overflow-hidden"
        data-testid={`${tid}-image`}
      >
        <img
          src={getProductImageMedium(product.sku, product.category)}
          alt={product.title || product.sku}
          loading="lazy"
          className="w-full h-full object-contain p-3 transition-transform duration-300 group-hover:scale-105"
        />
      </Link>

      {/* Countdown — solo offerte attive */}
      {isOffer && (
        <div className="px-3 -mt-3 relative z-10">
          <div
            className="bg-gradient-to-r from-[#1E4D8C] to-[#2B5EA7] text-white text-[11px] font-semibold py-1 px-2.5 rounded-full flex items-center justify-center gap-1.5 shadow-md"
            data-testid={`${tid}-countdown`}
          >
            <Timer className="w-3 h-3 animate-pulse" />
            <span className="tabular-nums">
              Restano {days > 0 ? `${days}g ` : ''}{String(hours).padStart(2, '0')}h {String(minutes).padStart(2, '0')}m {String(seconds).padStart(2, '0')}s
            </span>
          </div>
        </div>
      )}

      {/* Info prodotto */}
      <div className={`p-3 flex-1 flex flex-col ${!isOffer ? 'pt-4' : ''}`}>
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="text-[13px] lg:text-[10px] font-mono font-bold text-[#2B5EA7]">{product.sku}</span>
          <span className="text-[13px] lg:text-[9px] px-1.5 py-0.5 rounded border bg-secondary/60 font-medium">{product.brand}</span>
        </div>
        <Link
          to={`/prodotti/${product.category}/${product.sku}`}
          className="text-[13px] leading-snug font-semibold text-[#2B5EA7] underline-offset-2 hover:underline line-clamp-2 min-h-[34px]"
          data-testid={`${tid}-title`}
        >
          {product.title}
        </Link>

        {/* Prezzi */}
        {isAuthenticated ? (
          curNum > 0 ? (
            <>
              <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                <span className="text-xl font-bold text-[#2B5EA7] tabular-nums">€&nbsp;{priceCurrent}</span>
                {(isOffer || isOutlet) && priceOld && (
                  <span className="text-sm text-gray-600 line-through tabular-nums">€&nbsp;{priceOld}</span>
                )}
              </div>
              {(isOffer || isOutlet) && risparmio > 0 && (
                <div className="text-[11px] text-red-600 font-semibold flex items-center gap-1 mt-0.5">
                  <TrendingDown className="w-3 h-3" />
                  Risparmi €&nbsp;{risparmio.toFixed(2).replace('.', ',')}
                </div>
              )}
            </>
          ) : (
            <div className="mt-2 text-sm text-muted-foreground font-medium" data-testid={`${tid}-price-unavailable`}>
              Prezzo su richiesta
            </div>
          )
        ) : (
          <div className="mt-2 mb-1">
            <LoginToBuy />
          </div>
        )}
        {isOutlet && (
          <div className="text-[11px] text-red-600 font-semibold flex items-center gap-1 mt-0.5">
            <AlertCircle className="w-3 h-3" />
            In esaurimento
          </div>
        )}

        {/* Non vendibile online (``webSellable`` falso su NTS): non
            disponibile, nessuna giacenza e nessun acquisto (INT-112). */}
        {nonVendibile ? (
          <div className="mt-2.5 mb-2.5 flex items-center gap-1.5" data-testid={`${tid}-non-disponibile`}>
            <span className="w-2 h-2 rounded-full bg-gray-400" />
            <span className="text-[13px] lg:text-[10px] text-gray-500 font-semibold">Non disponibile</span>
          </div>
        ) : (
        /* Stock bar — 2 righe come da design originale. A giacenza zero
           l'etichetta diventa "Esaurito" e la barra resta vuota: la riga
           non sparisce, così la card resta alta come le vicine. */
        <div className="mt-2.5 mb-2.5" data-testid={`${tid}-stock`}>
          <div className="flex items-center justify-between mb-1">
            {stock > 0 ? (
              <>
                <span className="text-[13px] lg:text-[10px] text-emerald-700 font-semibold">In stock</span>
                <span className="text-[13px] lg:text-[9px] text-muted-foreground tabular-nums">{stock} disp.</span>
              </>
            ) : (
              <span className="text-[13px] lg:text-[10px] text-red-600 font-semibold">Esaurito</span>
            )}
          </div>
          <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all"
              style={{ width: `${stockPerc}%` }}
            />
          </div>
        </div>
        )}

        {/* CTA */}
        {nonVendibile ? (
          <button disabled className="mt-auto w-full h-9 rounded-lg bg-gray-100 text-gray-500 border border-gray-200 text-xs font-medium cursor-not-allowed">
            Non disponibile
          </button>
        ) : isAuthenticated ? (
          inCart ? (
            <div
              className="mt-auto w-full h-9 rounded-lg border-2 border-emerald-300 bg-emerald-50 flex items-center overflow-hidden"
              data-testid={`${tid}-stepper`}
            >
              <button
                onClick={() => updateQty(product.sku, cartQty - 1)}
                className="w-9 h-full flex items-center justify-center text-emerald-700 hover:bg-emerald-100 transition-colors flex-shrink-0"
                data-testid={`${tid}-decrement`}
                aria-label="Diminuisci quantità"
              >
                {cartQty <= 1 ? <Trash2 className="w-3.5 h-3.5 text-red-500" /> : <span className="text-sm font-bold">−</span>}
              </button>
              <span className="flex-1 text-center text-[11px] font-bold text-emerald-700 tabular-nums truncate px-1">
                {cartQty} nel carrello
              </span>
              <button
                onClick={() => {
                  if (cartQty + 1 > stock) {
                    toast.error(`Solo ${stock} pz disponibili`);
                    return;
                  }
                  addItem(product, 1);
                }}
                disabled={cartQty + 1 > stock}
                className="w-9 h-full flex items-center justify-center text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                data-testid={`${tid}-increment`}
                aria-label="Aumenta quantità"
              >
                <span className="text-sm font-bold">+</span>
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                if (addItem(product, 1) > 0) toast.success(`${product.sku} aggiunto al carrello`);
              }}
              disabled={stock <= 0}
              data-testid={`${tid}-buy`}
              className="mt-auto w-full h-9 rounded-lg bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-[#1E4D8C] text-xs font-bold flex items-center justify-center gap-1.5 transition-all shadow-sm hover:shadow-md disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              {stock <= 0 ? 'Esaurito' : 'Acquista ora'}
            </button>
          )
        ) : (
          <LoginToBuy variant="cta" />
        )}
      </div>
    </div>
  );
};
